import React from 'react'
import { LineChartElement } from './chartJS'
import { ChartProps } from 'react-chartjs-2/dist/types'
import { CHART_TOOLTIP_OPTIONS } from './chartWrapper'
import { Tooltip } from '../simple/tooltip'

export interface LineChartData {
  labels: string[]
  datasets: {
    label: string
    data: number[]
    color?: string
  }[]
}

interface LineChartProps {
  data: LineChartData
  legend?: boolean
  className?: string
}

export const LineChart: React.FC<LineChartProps> = ({ data, className, legend }) => {
  const processedOptions = React.useMemo(() => {
    const options: Omit<ChartProps<'line'>, 'type'> = {
      data: {
        labels: data.labels,
        datasets: data.datasets.map((d) => ({
          label: d.label,
          data: d.data,
          borderColor: d.color || 'transparent',
          tension: 0.1,
        })),
      },
      options: {
        elements: {
          point: {
            borderWidth: 0,
            radius: 15,
            backgroundColor: 'rgba(0,0,0,0)',
          },
        },
        scales: {
          x: {
            grid: {
              color: 'rgba(0, 0, 0, .03)',
            },
            ticks: {
              maxTicksLimit: 20,
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1,
            },
            grid: {
              color: 'rgba(0, 0, 0, .03)',
            },
          },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: legend
            ? {
                position: 'bottom',
                labels: {
                  boxHeight: 3,
                },
              }
            : {
                display: false,
              },
          tooltip: { ...CHART_TOOLTIP_OPTIONS, displayColors: false },
        },
      },
    }
    return options
  }, [data])
  return (
    <div className={className}>
      <LineChartElement {...processedOptions} />
    </div>
  )
}

export type SparklineChartData = {
  label: string
  value: number
}[]

export const SparklineChart: React.FC<{
  data: SparklineChartData
  className?: string
  color?: string
}> = ({ data, className, color = '#38bdf8' }) => {
  const processedOptions = React.useMemo(() => {
    const options: Omit<ChartProps<'line'>, 'type'> = {
      data: {
        labels: data.map((d) => d.label),
        datasets: [
          {
            label: '',
            data: data.map((d) => d.value),
            borderColor: color,
            tension: 0.1,
          },
        ],
      },
      options: {
        animation: false,
        elements: {
          point: {
            radius: 0,
          },
          line: {
            borderWidth: 2,
          },
        },
        scales: {
          x: {
            display: false,
          },
          y: {
            display: false,
          },
        },
        layout: {
          padding: {
            top: 10,
            bottom: 10,
            left: 0,
            right: 0,
          },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
        },
      },
    }
    return options
  }, [data])

  const [tooltip, setTooltip] = React.useState<string | undefined>(undefined)
  const [tooltipX, setTooltipX] = React.useState(0)

  return (
    <div className={`${className} relative max-h-14 max-w-32`}>
      <LineChartElement {...processedOptions} />
      {tooltip && (
        <Tooltip text={tooltip} open={true}>
          <div
            className="absolute bottom-3 left-0 top-3 z-10 w-px bg-gray-400/75"
            style={{
              transform: `translateX(${tooltipX}px)`,
            }}
          />
        </Tooltip>
      )}
      <div
        className="absolute inset-0 z-20"
        onMouseMove={(e) => {
          const { offsetX } = e.nativeEvent
          let index = Math.round((offsetX / e.currentTarget.clientWidth) * data.length)
          index = Math.min(data.length - 1, Math.max(0, index))
          setTooltip(`${data[index].label}: ${data[index].value}`)
          setTooltipX(offsetX)
        }}
        onMouseLeave={() => setTooltip(undefined)}
      />
    </div>
  )
}
